import Button from "components/common/Button";
import CustomToast from "components/common/CustomToast";
import Dropdown from "components/common/Dropdown";
import Loader from "components/common/Loader";
import ModalContainer from "components/common/ModalContainer";
import SynthTooltip from "components/common/SynthTooltip";
import useGetAllAiConversations from "hooks/Chat/useGetAllAiConversations";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import { truncate } from "lodash";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { chatActions } from "redux/Ldb/actions";
import { useMergedSearchParams } from "helpers/mergeSearchParams";
import { useNavigate } from "react-router-dom";
import { MediaAttachments } from "types/salesTargetType";

type ChatHistoryProps = {
    type: string;
    previousChatLayout: boolean;
    expandedId?: string;
    setExpandedId?: (expandedId: string) => void;
    reset: () => void;
    threadId: string;
    setThreadId: (threadId: string) => void;
    setCurrentChat?: (currentChat: string) => void;
    contentMessage: string;
    setContentMessage: (contentMessage: string) => void;
    setMessage: (message: string) => void;
    setMessages: (messages: any[]) => void;
    messages: any[];
    sendMessage: any;
    handleCreateConversation?: () => void;
    Messages?: () => React.ReactNode;
    page?: string;
    setFilesAttached?: (filesAttached: MediaAttachments[]) => void;
    setSelectedIds?: (selectedIds: { [key: string]: any }[]) => void;
};

const ChatHistory = ({
    type,
    previousChatLayout,
    expandedId,
    setExpandedId,
    reset,
    threadId,
    setThreadId,
    setCurrentChat,
    contentMessage,
    setContentMessage,
    setMessage,
    setMessages,
    messages,
    sendMessage,
    handleCreateConversation,
    Messages,
    page,
    setFilesAttached,
    setSelectedIds,
}: ChatHistoryProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const scrollContainer = document.getElementById("scrollContainer");
    const { updateSearchParams } = useMergedSearchParams();
    const renameInputRef = useRef<HTMLInputElement>(null);
    const [pinAction, setPinAction] = useState<string>("");
    const [confirmDeleteChat, setConfirmDeleteChat] = useState<boolean>(false);
    const [deleteId, setDeleteId] = useState<string>("");
    const [renameId, setRenameId] = useState<string>("");
    const [openInputId, setOpenInputId] = useState<string>("");
    const [newName, setNewName] = useState<string>("");
    const [oldName, setOldName] = useState<string>("");
    const [previousChat, setPreviousChat] = useState<{ [key: string]: any }>({});
    const [nextThreadId, setNextThreadId] = useState<string>("");
    const [refetch, setRefetch] = useState<boolean>(false);
    const [deleteThreadId, setDeleteThreadId] = useState<string>("");

    const { updateConversationSuccess, deleteConversationSuccess, deletingConversation } =
        useAppSelector((state) => state.chat);

    const {
        data: allConversations,
        isLoadingMore: loadingMoreConverstions,
        setUpdatedPage,
        isFetching,
    } = useGetAllAiConversations(scrollContainer, refetch);

    const groupedConversations = (allConversations: { [key: string]: any }[] | null) => {
        const pinned = allConversations.filter((c) => c.isPinned);
        const unpinned = allConversations.filter((c) => !c.isPinned);

        const sorted = [...pinned, ...unpinned];
        const grouped = sorted?.reduce(
            (acc: Record<string, typeof allConversations>, conversation) => {
                const days = moment().diff(moment(conversation?.updatedAt), "days");
                const month = moment(conversation?.updatedAt).format("MMMM YYYY");
                const isPinned = conversation?.isPinned;
                if (isPinned) {
                    acc["Pinned Chats"] = acc["Pinned Chats"] || [];
                    acc["Pinned Chats"].push(conversation);
                } else if (days <= 7) {
                    acc["Last 7 days"] = acc["Last 7 days"] || [];
                    acc["Last 7 days"].push(conversation);
                } else if (days <= 30) {
                    acc["Last 30 days"] = acc["Last 30 days"] || [];
                    acc["Last 30 days"].push(conversation);
                } else if (days > 30) {
                    acc[month] = acc[month] || [];
                    acc[month].push(conversation);
                }

                return acc;
            },
            {}
        );

        return grouped;
    };
    const getData = useMemo(() => {
        if (allConversations?.length > 0) {
            return groupedConversations(allConversations as { [key: string]: any }[]);
        }
    }, [allConversations]);

    useEffect(() => {
        if (Boolean(updateConversationSuccess)) {
            dispatch(chatActions.resetUpdateConversationSuccess());
            setUpdatedPage(1);
            setRefetch(!refetch);
            if (newName) {
                toast.custom((t) => (
                    <CustomToast t={t} message={"Chat renamed"} type={"success"} />
                ));
                setOldName("");
            } else {
                toast.custom((t) => (
                    <CustomToast
                        t={t}
                        title={pinAction === "Pinned" ? "Chat pinned" : "Chat unpinned"}
                        message={
                            pinAction === "Pinned"
                                ? "Chat is now pinned for quick access."
                                : "Chat is now unpinned."
                        }
                        type={"success"}
                    />
                ));
                setDeleteId("");
            }
        }
    }, [dispatch, updateConversationSuccess, pinAction]);

    useEffect(() => {
        setPreviousChat(getData);
        setNewName("");
        setOpenInputId("");
    }, [getData, allConversations]);
    //delete chat
    const handleDeleteChat = (conversationId: string) => {
        dispatch(chatActions.deleteConversation(conversationId));
    };

    useEffect(() => {
        if (Boolean(deleteConversationSuccess)) {
            setUpdatedPage(1);
            setRefetch(!refetch);
            if (nextThreadId && deleteThreadId === threadId) {
                navigate(`/dashboard/home/${nextThreadId}`);
                handleSelectChat(nextThreadId);
                setThreadId("");
                setMessages([]);
                setDeleteId("");
                setDeleteThreadId("");
            } else if (!nextThreadId) {
                setThreadId("");
                dispatch(chatActions.fetchChatThread(""));
                dispatch(chatActions.resetFetchChatThreadSuccess());
                setMessages([]);
                setDeleteId("");
                if (type === "flow") {
                    navigate(`/dashboard/home`);
                } else {
                }
            }

            setConfirmDeleteChat(false);
            toast.custom((t) => (
                <CustomToast
                    t={t}
                    title={"Chat deleted"}
                    message={deleteConversationSuccess?.message}
                    type={"success"}
                />
            ));
            dispatch(chatActions.resetDeleteConversationSuccess());
        }
    }, [dispatch, deleteConversationSuccess, nextThreadId]);

    const handleRenameChat = () => {
        if (newName !== oldName && newName !== "") {
            dispatch(
                chatActions.updateConversation({
                    title: newName || "New chat",
                    conversationId: renameId,
                })
            );
            setRenameId("");
        } else {
            setRenameId("");
            setNewName("");
            setOldName("");
            setOpenInputId("");
        }
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                renameId &&
                renameInputRef?.current &&
                !renameInputRef?.current?.contains(e.target as Node)
            ) {
                handleRenameChat();
            }
        };

        window.addEventListener("mousedown", handleClickOutside);

        return () => {
            window.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dispatch, newName, renameId]);

    useEffect(() => {
        if (renameId && renameInputRef.current) {
            renameInputRef.current.focus();
        }
    }, [renameId]);

    //pin chat
    const handlePinChat = (conversationId: string, pin) => {
        const body = {
            isPinned: pin,
            conversationId: conversationId,
        };

        dispatch(chatActions.updateConversation(body));
    };

    const handleSelectChat = (id: string) => {
        if (threadId === id) return;
        reset();
        dispatch(chatActions.fetchChatThread(id));
        setMessage("");
        setFilesAttached?.([]);
        setSelectedIds?.([]);
        navigate(window.location.pathname, { replace: true, state: {} });
    };

    return (
        <div className={`${type === "salesAgent" && "bg-white rounded"}`}>
            <div
                className={`bg-white rounded-[8px] relative transition-all duration-100 ease-in-out ${
                    type === "flow" && ""
                }   ${
                    previousChatLayout && type === "flow"
                        ? " h-[calc(100vh-160px)] px-5 opacity-100 !w-[387px] bg-white border border-slate-100  shadow-[0px_4px_6px_-2px_rgba(16,24,40,0.02),0px_12px_16px_-4px_rgba(16,24,40,0.06)]"
                        : type === "salesAgent"
                        ? "opacity-100 !w-[250px]"
                        : "opacity-0 !w-0 !h-0 "
                }`}
            >
                <div
                    className={` w-full py-3 border-b border-slate-200 ${
                        type === "flow" && "border-dashed"
                    }`}
                >
                    {type === "flow" ? (
                        <p className="text-base font-rocGroteskMedium text-slate-600 ">
                            {expandedId ? "Chat" : "Chat history"}
                        </p>
                    ) : (
                        <p className="text-base font-rocGroteskMedium text-g-75 px-5">
                            All Sales target
                        </p>
                    )}
                </div>

                <div className={`py-3 font-rocGroteskMedium ${type === "salesAgent" && "px-5"}`}>
                    {!expandedId &&
                        (messages?.length > 0 || type === "flow" ? (
                            <div
                                className="bg-[#F4F5F7] rounded px-4 py-2 text-center cursor-pointer flex gap-2 items-center justify-center"
                                onClick={() => {
                                    if (type === "flow") {
                                        reset();
                                        setExpandedId?.("");
                                        updateSearchParams({
                                            updated: "",
                                        });
                                        setMessages([]);
                                        navigate(`/dashboard/home`);
                                    } else {
                                        handleCreateConversation();
                                        updateSearchParams({
                                            updated: "true",
                                        });
                                    }
                                    setMessage("");
                                }}
                            >
                                <i className="ri-add-line text-base text-gm-50 "></i>
                                <p className="text-gm-50 text-[13px] ">New chat</p>
                            </div>
                        ) : (
                            <SynthTooltip
                                divId={"new-sales-target-tooltip"}
                                position={"bottom"}
                                icon={
                                    <div className="bg-[#F4F5F7] rounded px-4 py-2 text-center  flex gap-2 items-center justify-center">
                                        <i className="ri-add-line text-base text-gm-50 "></i>
                                        <p className="text-gm-50 text-[13px] ">New chat</p>
                                    </div>
                                }
                                content="You're already in a new chat. Finish or close this conversation before starting a new one."
                                styling={{
                                    width: "232px",
                                    background: "#142837",
                                    borderRadius: "6px",
                                }}
                                contentClass="text-xs text-white"
                            />
                        ))}

                    <div
                        className={` space-y-3 ${
                            expandedId && type !== "salesAgent"
                                ? "mt-0 "
                                : !expandedId && type !== "salesAgent"
                                ? "mt-6 h-[calc(100vh-350px)] max-h-[1200px] overflow-y-auto"
                                : "max-h-[620px] h-[70vh] mt-6 overflow-y-auto"
                        } text-sm font-rocGroteskMedium`}
                        id="scrollContainer"
                    >
                        {isFetching &&
                            previousChat != null &&
                            Object.keys(previousChat).length === 0 && (
                                <div className="flex items-center justify-center my-5">
                                    <Loader />
                                </div>
                            )}

                        {expandedId
                            ? Messages()
                            : previousChat &&
                              Object?.entries(previousChat)?.length > 0 &&
                              Object?.entries(previousChat)?.map(([key, value]) => {
                                  return (
                                      <div
                                          className="flex flex-col items-start justify-between"
                                          key={key}
                                      >
                                          <p className="text-sm text-g-75 p-2">{key}</p>
                                          <div className="w-full space-y-1 ">
                                              {value?.map((msg, id) => {
                                                  const nextId = value[id + 1]?.threadId
                                                      ? value[id + 1]?.threadId
                                                      : value[id - 1]?.threadId
                                                      ? value[id - 1]?.threadId
                                                      : "";

                                                  return (
                                                      <div className="" key={id}>
                                                          {renameId === msg?._id ? (
                                                              <div className="p-2 border-[#FFB902] border-[1.5px] rounded-md ">
                                                                  <input
                                                                      name="name"
                                                                      value={newName}
                                                                      onChange={(e) => {
                                                                          e.stopPropagation();
                                                                          setNewName(
                                                                              e.target.value
                                                                          );
                                                                      }}
                                                                      disabled={
                                                                          renameId !== msg?._id
                                                                      }
                                                                      ref={renameInputRef}
                                                                      className="!w-full !outline-none  !text-g-75 bg-[#CCEBFD]"
                                                                      onKeyDown={(e) => {
                                                                          if (e.key === "Enter") {
                                                                              handleRenameChat();
                                                                          }
                                                                      }}
                                                                  />
                                                              </div>
                                                          ) : (
                                                              <div
                                                                  key={id}
                                                                  id="chat-item"
                                                                  className={`text-sm text-slate-500 hover:bg-slate-100 hover:text-g-75
                                                                          rounded p-2 cursor-pointer group flex items-center justify-between w-full relative ${
                                                                              threadId ===
                                                                                  msg?.threadId &&
                                                                              page !== "flow"
                                                                                  ? "bg-slate-100"
                                                                                  : ""
                                                                          } `}
                                                                  onClick={() => {
                                                                      if (type === "flow") {
                                                                          navigate(
                                                                              `/dashboard/home/${msg?.threadId}`
                                                                          );
                                                                      } else {
                                                                          updateSearchParams({
                                                                              updated: "true",
                                                                          });
                                                                      }
                                                                      reset();
                                                                      handleSelectChat(
                                                                          msg?.threadId
                                                                      );
                                                                      setCurrentChat?.(
                                                                          newName &&
                                                                              openInputId ===
                                                                                  msg?._id &&
                                                                              newName !== ""
                                                                              ? newName
                                                                              : msg?.title ||
                                                                                    msg?.lastMessage
                                                                                        ?.message ||
                                                                                    ""
                                                                      );
                                                                  }}
                                                              >
                                                                  <p className="">
                                                                      {truncate(
                                                                          newName &&
                                                                              openInputId ===
                                                                                  msg?._id &&
                                                                              newName !== ""
                                                                              ? newName
                                                                              : msg?.title ||
                                                                                    msg?.lastMessage
                                                                                        ?.message ||
                                                                                    "New Chat",
                                                                          {
                                                                              length:
                                                                                  type === "flow"
                                                                                      ? 35
                                                                                      : 23,
                                                                          }
                                                                      )}
                                                                  </p>

                                                                  <div
                                                                      className={`transition-all duration-300 relative !z-[99] ${
                                                                          threadId ===
                                                                              msg?.threadId &&
                                                                          page !== "flow"
                                                                              ? "opacity-100"
                                                                              : "opacity-0 group-hover:opacity-100"
                                                                      } `}
                                                                      onClick={(e) => {
                                                                          e.stopPropagation();
                                                                      }}
                                                                  >
                                                                      <Dropdown
                                                                          value={""}
                                                                          dropdown={
                                                                              <i className="ri-more-2-fill text-base text-g-75 !z-0 relative"></i>
                                                                          }
                                                                          dropdownItemsClasses="!border-t !border-t-slate-100 !py-2"
                                                                          dropdownClassName={`shadow-cardShadow-4 -right-0  border border-slate-100 !w-[159px] rounded !max-h-fit`}
                                                                          dropdownContainerClasses={`shadow-cardShadow-4 -right-0  border border-slate-100 !w-[159px] rounded !max-h-fit !py-0`}
                                                                          name={"faq-dropdown"}
                                                                          dropdownOptions={[
                                                                              {
                                                                                  label: msg?.isPinned
                                                                                      ? "Unpin chat"
                                                                                      : "Pin chat",
                                                                                  value: "pin",
                                                                                  icon: (
                                                                                      <i className="ri-pushpin-line text-base text-g-75 "></i>
                                                                                  ),
                                                                                  action: () => {
                                                                                      setPinAction(
                                                                                          msg?.isPinned
                                                                                              ? "Unpinned"
                                                                                              : "Pinned"
                                                                                      );

                                                                                      msg?.isPinned
                                                                                          ? handlePinChat(
                                                                                                msg?._id,
                                                                                                false
                                                                                            )
                                                                                          : handlePinChat(
                                                                                                msg?._id,
                                                                                                true
                                                                                            );
                                                                                  },
                                                                              },
                                                                              {
                                                                                  label: "Rename chat",
                                                                                  value: "rename",
                                                                                  action: () => {
                                                                                      setRenameId(
                                                                                          msg?._id
                                                                                      );
                                                                                      setOpenInputId(
                                                                                          msg?._id
                                                                                      );
                                                                                      setOldName(
                                                                                          msg?.title ||
                                                                                              msg
                                                                                                  ?.lastMessage
                                                                                                  ?.message ||
                                                                                              "New chat"
                                                                                      );
                                                                                      setNewName(
                                                                                          msg?.title ||
                                                                                              msg
                                                                                                  ?.lastMessage
                                                                                                  ?.message ||
                                                                                              "New chat"
                                                                                      );
                                                                                  },
                                                                                  icon: (
                                                                                      <i className="ri-edit-line text-base text-g-75 "></i>
                                                                                  ),
                                                                              },
                                                                              {
                                                                                  label: "Delete chat",
                                                                                  value: "delete",
                                                                                  action: () => {
                                                                                      setConfirmDeleteChat(
                                                                                          true
                                                                                      );
                                                                                      setDeleteId(
                                                                                          msg?._id
                                                                                      );
                                                                                      setNextThreadId(
                                                                                          nextId
                                                                                      );
                                                                                      setDeleteThreadId(
                                                                                          msg?.threadId
                                                                                      );
                                                                                  },
                                                                                  icon: (
                                                                                      <i className="ri-delete-bin-line text-base text-[#E4281D] "></i>
                                                                                  ),
                                                                                  labelClassName:
                                                                                      "!text-[#E4281D] !text-sm",
                                                                              },
                                                                          ]}
                                                                      />
                                                                  </div>
                                                              </div>
                                                          )}
                                                      </div>
                                                  );
                                              })}
                                          </div>
                                      </div>
                                  );
                              })}

                        {!expandedId && loadingMoreConverstions && (
                            <div className="flex items-center justify-center my-5">
                                <Loader />
                            </div>
                        )}

                        {type === "flow" && typeof previousChat === "undefined" && (
                            <div className="flex items-center justify-center my-5">
                                <p className="text-sm text-g-75">No chat history found</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <ModalContainer
                open={confirmDeleteChat}
                modalClassName={"py-10 px-10"}
                tailwindClassName="md:w-[600px] w-[90%] "
                showCloseIcon={false}
                closeModal={() => {
                    setConfirmDeleteChat(false);
                }}
            >
                <div className="bg-white py-7 px-6  shadow-cardShadow  rounded-[8px]">
                    <div className="mb-5 mt-4">
                        <h1 className="text-lg mb-2  font-rocGroteskMedium">Delete this chat?</h1>
                        <p className="text-slate-500 font-rocGroteskMedium text-base">
                            This action will permanently delete the entire conversation. You won't
                            be able to recover it later.
                        </p>
                    </div>
                    <div className="flex justify-end items-center ">
                        <div className="flex items-center !whitespace-nowrap space-x-2  ">
                            <Button
                                btnText={"No, cancel"}
                                type={"button"}
                                btnClassname={"!bg-n-20 !w-fit !text-gm-50"}
                                onClick={() => setConfirmDeleteChat(false)}
                            />
                            <Button
                                btnClassname={"!py-3 !w-fit !bg-[#E4281D] !text-white"}
                                onClick={() => {
                                    handleDeleteChat(deleteId);
                                }}
                                btnText={"Yes, delete chat"}
                                type={"button"}
                                isLoading={deletingConversation}
                            />
                        </div>
                    </div>
                </div>
            </ModalContainer>
        </div>
    );
};

export default ChatHistory;
